<template>
  <div class="dashboard-container">
    <!-- Loader: Top Progress Bar -->
    <div
      v-if="loading"
      class="progress-bar"
      :style="{ width: progress + '%' }"
    ></div>

    <div class="container">
      <!-- Form Title -->
      <div class="mb-2 page-title">
        <i class="bi bi-file-earmark-text"></i>
        {{ isEdit ? "Edit Match Category" : "Add Match Category" }}
      </div>

      <!-- Form -->
      <form @submit.prevent="submitForm" class="admin-form mt-4">
        <div class="row">
          <div class="col-lg-12">
            <div class="mb-3">
              <label for="tournament_id" class="form-label">Tournament</label>
              <select
                class="form-select"
                id="tournament_id"
                v-model="form.tournament_id"
                :class="{ 'is-invalid': errors.tournament_id }"
              >
                <option value="" disabled>Choose Tournament</option>
                <option 
                  v-for="tournament in allTournaments" 
                  :key="tournament.id" 
                  :value="tournament.id"
                >
                  {{ tournament.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.tournament_id }}</div>
            </div>
            <div class="mb-3">
              <label for="match_category_id" class="form-label">Match Category</label>
              <select
                class="form-select"
                id="match_category_id"
                v-model="form.match_category_id"
                :class="{ 'is-invalid': errors.match_category_id }"
              >
                <option value="" disabled>Choose Match Category</option>
                <option 
                  v-for="matchCategory in matchCategories" 
                  :key="matchCategory.id" 
                  :value="matchCategory.id"
                >
                  {{ matchCategory.name }}
                </option>
              </select>
              <div class="invalid-feedback">{{ errors.match_category_id }}</div>
            </div>
            <div class="mb-3">
              <label for="registration_fee" class="form-label">Registration Fee</label>
              <input
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.registration_fee }"
                id="registration_fee"
                v-model="form.registration_fee"
                 @input="onInputRegistrationFee"
              />
              <div class="invalid-feedback">{{ errors.registration_fee }}</div>
            </div>
          </div>
        </div>
       
        
             

        <!-- Submit Button -->
        <div class="row">
          <div class="col-lg-12 text-center">
            <button type="submit" class="button button-primary" :disabled="loading">
              <i class="bi bi-floppy"></i>
              <span>{{ isEdit ? "Update Match Category" : "Add Match Category" }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { useToast } from "vue-toastification";

export default {
  name: "CategoryForm",
  props: {
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      activityId: null,
      matchCategories: [],
      allTournaments: [],
      form: {
        tournament_id: "",
        match_category_id: "",
        registration_fee: ""
      },
      errors: {},
      loading: false,
      progress: 0,
    };
  },

  created() {
    this.classId = this.$route.params.id;
    this.fetchActiveTournaments();
    this.fetchMatchCategories();
    if (this.isEdit && this.classId) {
      this.fetchActivityDetail(this.classId);
    }
  },

  beforeRouteUpdate(to, from, next) {
    if (this.isEdit && to.params.id !== this.memberId) {
      this.memberId = to.params.id;
      this.fetchMemberDetail(this.memberId);
    }
    next();
  },

  watch: {
    // Watch for changes to the memberId from the route params
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        // Fetch contingent data when route param changes
        if (this.isEdit && newId) {
          this.memberId = newId;
          this.fetchActivityDetail(newId); 
        }
      },
    }
  },

  methods: {

    formatCurrency(value) {
      const number = parseInt(value);
      return new Intl.NumberFormat('id-ID', {
        maximumFractionDigits: 0, // remove .00
      }).format(number);
    },
    onInputRegistrationFee(e) {
      const raw = e.target.value.replace(/\D/g, '');
      this.form.registration_fee = this.formatCurrency(raw);
    },
    async fetchActiveTournaments() {
      await this.fetchData("/tournaments/all", "allTournaments");
    },

    async fetchMatchCategories() {
      try {
        const response = await axios.get("/match-categories");
        this.matchCategories = response.data;
      } catch (error) {
        console.error("Error fetching match-categories:", error);
      }
    },

    async fetchData(url, targetKey) {
      try {
        const response = await axios.get(url);
        this[targetKey] = response.data;
      } catch (error) {
        this.toast.error(`Failed to fetch ${targetKey}`);
      }
    },
    async fetchActivityDetail(id) {
      this.loading = true;
      try {
        const response = await axios.get(
          `/tournament-match-categories/${id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('authToken')}`
            }
          }
        );
        if (response.data) {
          const detail = response.data.data;
          this.form = {
            tournament_id : detail.tournament_id || "",
            match_category_id: detail.match_category_id || "",
            registration_fee: this.formatCurrency(detail.registration_fee || 0),
          };

          
        }
      } catch (error) {
        this.toast.error("Error fetching class details.");
        console.error("Error fetching class details:", error);
      } finally {
        this.loading = false;
      }
    },

    
    async submitForm() {
      this.loading = true;

      try {
        const endpoint = this.isEdit
          ? `/tournament-match-categories/${this.memberId}`
          : "/tournament-match-categories";
        const method = this.isEdit ? "put" : "post";

        // Format registration_fee sebelum dikirim
        const payload = {
          tournament_id: this.form.tournament_id,
          match_category_id: this.form.match_category_id,
          registration_fee: parseInt(this.form.registration_fee.replace(/\./g, "")) || '',
        };

        await axios[method](endpoint, payload, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });

        this.toast.success(
          this.isEdit
            ? "Tournament Match Category updated successfully!"
            : "Tournament Match Category added successfully!"
        );
        this.$router.push("/admin/tournament-category");
      } catch (error) {
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
          this.toast.error("An error occurred while submitting the form.");
        } else {
          this.toast.error("An error occurred while submitting the form.");
        }
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>


<style scoped>
.dashboard-container {
  background-color: #ffffff;
  margin-top: 80px;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative; /* For positioning progress bar */
}

.progress-bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  background-color: #388E3C;
  animation: loader-animation 1.5s infinite;
}

.page-title {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 20px;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
}

/* Animasi garis loader */
@keyframes loader-animation {
  0% {
    width: 0;
    background-color: #388E3C; /* Warna awal */
  }
  50% {
    width: 50%;
    background-color: #388E3C; /* Warna saat animasi */
  }
  100% {
    width: 100%;
    background-color: #388E3C;
  }
}
</style>
